import './styles.scss';

import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Text, Table, StatusChip, Input, Popper, Button } from '@fragments';
import TabsSelector from '@components/TabsSelector';
import {
    IconClipboardList,
    IconFilePlus,
    IconCopy,
    IconSearch,
    IconSettings,
    IconTrash,
    IconDots,
    IconDownload,
    IconPencil,
    IconShare,
    IconForms,
    IconClipboardText,
} from '@tabler/icons-react';

export default function FormsView({
    forms,
    loading,
    page,
    pages,
    records,
    search,
    handleSearch,
    handleSelectPage,
    onCreateFromScratch,
    onUseTemplate,
    onEditForm,
    onDuplicateForm,
    onToggleActive,
    onDeleteForm,
    subTabs,
    activeSubTab,
    activeComponent,
    onSubTabChange,
}) {
    const navigate = useNavigate();
    const { slug } = useParams();
    const popperRef = useRef();

    function openMenu(event, item) {
        popperRef.current.open(
            [
                {
                    id: 'edit',
                    label: 'Editar formulário',
                    icon: <IconPencil size={15} />,
                    onClick: async () => onEditForm?.(item),
                },
                {
                    id: 'duplicate',
                    label: 'Duplicar formulário',
                    icon: <IconCopy size={15} />,
                    onClick: async () => onDuplicateForm?.(item),
                },
                {
                    id: 'download',
                    label: 'Baixar configurações',
                    icon: <IconDownload size={15} />,
                    onClick: async () => downloadForm(item),
                },
                { divider: true, id: 'divider-1' },
                {
                    id: 'delete',
                    label: 'Excluir formulário',
                    icon: <IconTrash size={15} />,
                    variant: 'danger',
                    onClick: async () => confirmDelete(item),
                },
            ],
            'bottom',
            event.currentTarget,
        );
    }

    function confirmDelete(item) {
        window.openConfirm({
            title: 'Excluir formulário',
            headline: 'Atenção!',
            subtitle: `Você realmente deseja excluir permanentemente o formulário "${item.title}"?`,
            buttons: [
                {
                    text: 'Excluir permanentemente',
                    onClick: () => onDeleteForm?.(item),
                    variant: 'contained',
                    color: 'error',
                    className: '',
                },
                {
                    text: 'Cancelar',
                    variant: 'outlined',
                    className: '',
                },
            ],
        });
    }

    function downloadForm(item) {
        const { id, community_id, return_url, banner, ...templateData } = item;

        const cleanedData = {
            ...templateData,
            actions: [],
            steps: (templateData.steps ?? []).map(({ id: _sid, ...step }) => ({
                ...step,
                questions: (step.questions ?? []).map(({ id: _qid, ...question }) => question),
            })),
        };

        const sanitizedTitle = (item.title || 'formulario').replace(/[\\/:*?"<>|]/g, '').trim();
        const filename = `${sanitizedTitle} - Template.json`;
        const blob = new Blob([JSON.stringify(cleanedData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const columns = [
        {
            id: 'title',
            title: 'Formulário',
            grow: 1,
            customRender: (item) => (
                <div className="form-title-cell">
                    <div className="form-title-icon">
                        <IconClipboardList size={16} />
                    </div>
                    <Text.span contrast={8}>{item.title || '—'}</Text.span>
                </div>
            ),
        },
        {
            id: 'answers_count',
            title: 'Respostas',
            width: '100px',
            align: 'center',
            customRender: (item) => (
                <Text.span contrast={5}>{String(item.answers_count ?? 0)}</Text.span>
            ),
        },
        {
            id: 'return_url',
            title: 'URL de retorno',
            width: '220px',
            customRender: (item) =>
                item.return_url
                    ? <Text.span contrast={5} className="return-url-cell">{item.return_url}</Text.span>
                    : <Text.span contrast={3}>—</Text.span>,
        },
    ];

    return (
        <div className="forms-tab px-4">

            <div className='mt-4'>
                <Text.h2 left={<IconClipboardText />} weight={500} className="mb-1">Meus formulários</Text.h2>
                <Text.span contrast={6} style={{ paddingLeft: '44px' }}>Envie formulários para sua audiência, e colete informações customizadas por você.</Text.span>

            </div>



            <div className="mb-3">
                <TabsSelector
                    tabs={subTabs}
                    value={activeSubTab}
                    onChange={onSubTabChange}
                />
            </div>

            {activeSubTab === 'list' ? (
                <>
                    {/* ── Creation cards ─────────────────────────────────── */}
                    <div className="forms-creation-row">
                        <CreationCard
                            icon={<IconFilePlus size={22} />}
                            title="Criar formulário do zero"
                            description="Monte seu formulário personalizando cada etapa e pergunta"
                            onClick={onCreateFromScratch}
                        />
                        <CreationCard
                            icon={<IconCopy size={22} />}
                            title="Usar um template"
                            description="Comece a partir de um modelo pronto e adapte ao seu contexto"
                            onClick={onUseTemplate}
                        />
                        <SearchCard onClick={onUseTemplate} />
                    </div>

                    {/* ── Forms table ────────────────────────────────────── */}
                    <div className="forms-table-section">
                        <div className="forms-table-header">
                            <Text.h5 contrast={7}>
                                {loading ? 'Carregando...' : `${records} formulário${records !== 1 ? 's' : ''}`}
                            </Text.h5>
                        </div>

                        <Table
                            id="community-forms-table"
                            columns={columns}
                            data={forms}
                            loading={loading}
                            loadingRows={5}
                            enablePagination={pages > 1}
                            page={page}
                            pages={pages}
                            records={records}
                            contained={true}
                            onSelectPage={handleSelectPage}
                            emptyMessage="Nenhum formulário encontrado"
                            emptySubtitle="Crie seu primeiro formulário para começar a coletar respostas"
                            emptyIcon={<IconClipboardList size={36} />}
                            actionsComponent={(item) => (
                                <div className="form-row-actions">
                                    <Button
                                        size="small"
                                        color="overlay"
                                        startIcon={<IconClipboardText size={16} />}
                                        onClick={() => navigate(`/${slug}/settings/forms/submissions?form_id=${item.id}`)}
                                    >
                                        Ver submissões
                                    </Button>
                                    <Button
                                        size="small"
                                        color="overlay"
                                        startIcon={<IconShare />}
                                        onClick={() => {
                                            const link = `https://forms.ginastee.com/${item.id}`;
                                            navigator.clipboard.writeText(link).then(() => {
                                                window.setSnackbar?.('Link copiado!');
                                            });
                                        }}
                                    >
                                        Compartilhar
                                    </Button>
                                    <Input.Switch
                                        value={!!item.active}
                                        onInput={(val) => onToggleActive?.(item, val)}
                                        size="small"
                                        className='toggle-form-state-table'
                                        label={!!item.active ? 'Ativo' : 'Inativo'}
                                    />
                                    <Button
                                        size="small"
                                        color="text"
                                        icon={<IconSettings size={16} />}
                                        onClick={() => onEditForm?.(item)}
                                    />
                                    <Button
                                        size="small"
                                        color="text"
                                        icon={<IconDots size={16} />}
                                        onClick={(e) => openMenu(e, item)}
                                    />
                                </div>
                            )}
                        />
                        <Popper controlRef={popperRef} />
                    </div>
                </>
            ) : (
                activeComponent
            )}
        </div>
    );
}

function CreationCard({ icon, title, description, disabled, onClick }) {
    return (
        <div
            className={`forms-creation-card${disabled ? ' forms-creation-card--disabled' : ''}`}
            onClick={!disabled ? onClick : undefined}
        >
            <div className="fcc-icon">{icon}</div>
            <div className="fcc-body">
                <Text.h6 contrast={8}>{title}</Text.h6>
                <Text.span contrast={5}>{description}</Text.span>
            </div>
        </div>
    );
}

function SearchCard({ onClick }) {
    return (
        <div className="forms-creation-card" onClick={onClick}>
            <div className="fcc-icon"><IconSearch size={22} /></div>
            <div className="fcc-body">
                <Text.h6 contrast={8}>Explorar modelos</Text.h6>
                <Text.span contrast={5}>Descubra templates criados pela comunidade</Text.span>
            </div>
        </div>
    );
}
